import React, { useEffect, useRef, useState } from "react";
import api, { API, formatIDR } from "../lib/api";
import Layout from "../components/Layout";
import { RefreshCw, Navigation, Undo2, Check, SettingsIcon, FileSpreadsheet } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import CustomerFormModal, { customerToForm, formToCustomerPayload } from "../components/CustomerFormModal";

const monthNow = () => new Date().toISOString().slice(0, 7);
const METHOD_LABEL = {
  osrm: "rute jalan",
  haversine: "garis lurus ×1.55",
  manual: "manual",
  no_coords: "tanpa koordinat",
  no_office: "tanpa kantor",
  geocode_alamat: "estimasi alamat",
  geocode_kota: "estimasi kota",
  geocode_far: "terlalu jauh dari kantor",
};

export default function Mileage() {
  const [month, setMonth] = useState(monthNow());
  const [techId, setTechId] = useState("");
  const [techs, setTechs] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [edits, setEdits] = useState({}); // order_id -> km string
  const [savingId, setSavingId] = useState(null);
  const reqSeq = useRef(0);

  useEffect(() => {
    api.get("/technicians").then((r) => setTechs((r.data || []).filter((t) => (t.role || "teknisi") !== "staff"))).catch(() => {});
  }, []);

  const [custEdit, setCustEdit] = useState(null); // { id, initial }
  const [custSaving, setCustSaving] = useState(false);
  const openCustomer = async (id) => {
    if (!id) return;
    try {
      const { data: c } = await api.get(`/customers/${id}/detail`);
      setCustEdit({ id, initial: customerToForm(c) });
    } catch (_) {
      toast.error("Data pelanggan tidak ditemukan");
    }
  };
  const saveCustomer = async (form) => {
    setCustSaving(true);
    try {
      await api.put(`/customers/${custEdit.id}`, formToCustomerPayload(form));
      toast.success("Pelanggan diperbarui — jarak dihitung ulang");
      setCustEdit(null);
      load();
    } catch (_) {
      toast.error("Gagal menyimpan pelanggan");
    } finally {
      setCustSaving(false);
    }
  };

  const load = async (refresh = 0) => {
    const seq = ++reqSeq.current;
    setLoading(true);
    try {
      const { data: d } = await api.get("/mileage", { params: { month, refresh, technician_id: techId } });
      if (seq !== reqSeq.current) return;
      setData(d);
      setEdits({});
      if (refresh) toast.success("Jarak dihitung ulang");
    } catch (err) {
      if (seq === reqSeq.current) toast.error(err?.response?.data?.detail || "Gagal memuat jarak tempuh");
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month, techId]);

  const downloadExcel = () => {
    const token = localStorage.getItem("om_token");
    window.open(`${API}/mileage/export.xlsx?month=${month}&technician_id=${encodeURIComponent(techId)}&token=${encodeURIComponent(token || "")}`, "_blank");
  };

  const saveManual = async (orderId, km) => {
    setSavingId(orderId);
    try {
      await api.put(`/mileage/${orderId}`, { manual_km: km });
      toast.success(km === null ? "Kembali ke jarak otomatis" : "Jarak manual disimpan");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menyimpan jarak");
    } finally {
      setSavingId(null);
    }
  };

  const items = data?.items || [];
  const configMissing = data && (!data.bbm_price || !data.motor_consumption);
  const reimbursements = techId ? (data?.reimbursements || []) : [];
  const reimByDate = reimbursements.reduce((acc, r) => { (acc[r.date] = acc[r.date] || []).push(r); return acc; }, {});
  const itemDates = new Set(items.map((it) => it.schedule_date));
  const orphanReims = reimbursements.filter((r) => !itemDates.has(r.date));
  const rowKey = (it) => `${it.order_id}|${it.schedule_date}`;
  const firstRowOfDate = new Set();
  {
    const seen = new Set();
    for (const it of items) {
      if (!seen.has(it.schedule_date)) { seen.add(it.schedule_date); firstRowOfDate.add(rowKey(it)); }
    }
  }

  return (
    <Layout>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-black tracking-tight flex items-center gap-2">
            <Navigation className="text-[#002FA7]" size={28} /> JARAK TEMPUH
          </h1>
          <p className="text-xs text-[#52525B] mt-1">
            Jarak kantor cabang (otomatis dari kota client; bila tidak dikenali pakai base teknisi) → lokasi pelanggan. Rumus: Liter = (Jarak PP × 1.1) ÷ konsumsi motor, Uang Bensin = Liter × harga BBM. Kendaraan Mobil tidak dihitung.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select data-testid="mileage-tech-filter" value={techId} onChange={(e) => setTechId(e.target.value)} className="px-3 py-2 border border-[#E4E4E7] text-sm outline-none focus:border-[#002FA7] bg-white max-w-[180px]">
            <option value="">Semua Teknisi</option>
            {techs.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <input type="month" data-testid="mileage-month-input" value={month} onChange={(e) => setMonth(e.target.value)} className="px-3 py-2 border border-[#E4E4E7] text-sm font-mono outline-none focus:border-[#002FA7] bg-white" />
          <button data-testid="mileage-refresh-btn" onClick={() => load(1)} disabled={loading} className="px-3 py-2 border border-[#E4E4E7] bg-white text-xs font-bold uppercase flex items-center gap-1.5 hover:bg-[#F4F4F5] disabled:opacity-50">
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Hitung Ulang
          </button>
          <button data-testid="mileage-excel-btn" onClick={downloadExcel} disabled={loading || !items.length} className="px-3 py-2 bg-[#16A34A] text-white text-xs font-bold uppercase flex items-center gap-1.5 hover:bg-[#15803D] disabled:opacity-50">
            <FileSpreadsheet size={13} /> Excel
          </button>
        </div>
      </div>

      {configMissing && (
        <div data-testid="mileage-config-warning" className="border border-[#F59E0B] bg-[#F59E0B]/10 px-4 py-3 mb-4 text-xs">
          <b>Harga BBM / Konsumsi Motor belum diisi</b> — uang bensin akan 0. Isi di{" "}
          <Link to="/settings" className="text-[#002FA7] font-bold underline inline-flex items-center gap-1"><SettingsIcon size={11} /> Pengaturan</Link>.
        </div>
      )}

      <div className={`grid grid-cols-2 ${techId ? "lg:grid-cols-5" : "lg:grid-cols-4"} gap-3 mb-6`}>
        <div className="bg-white border border-[#E4E4E7] p-4 sm:p-5">
          <div className="label-cap text-[10px]">Total Order</div>
          <div data-testid="mileage-total-orders" className="text-2xl sm:text-3xl font-black font-mono mt-1">{data?.totals?.orders ?? "—"}</div>
        </div>
        <div className="bg-white border border-[#E4E4E7] p-4 sm:p-5">
          <div className="label-cap text-[10px]">Total Jarak PP</div>
          <div data-testid="mileage-total-km" className="text-2xl sm:text-3xl font-black font-mono mt-1">{data ? `${(data.totals?.km_pp || 0).toLocaleString("id-ID")} km` : "—"}</div>
        </div>
        <div className="bg-white border border-[#E4E4E7] p-4 sm:p-5">
          <div className="label-cap text-[10px]">Total Uang Bensin</div>
          <div data-testid="mileage-total-cost" className="text-lg sm:text-2xl font-black font-mono mt-1 text-[#002FA7]">{data ? formatIDR(data.totals?.cost || 0) : "—"}</div>
        </div>
        {techId && (
          <div className="bg-white border border-[#E4E4E7] p-4 sm:p-5">
            <div className="label-cap text-[10px]">Total Reimbursement</div>
            <div data-testid="mileage-total-reimburse" className="text-lg sm:text-2xl font-black font-mono mt-1 text-[#7C3AED]">{data ? formatIDR(data.totals?.reimburse || 0) : "—"}</div>
            <div className="text-[9px] text-[#A1A1AA] mt-0.5">{reimbursements.length} entri · HadirApp</div>
          </div>
        )}
        <div className="bg-white border border-[#E4E4E7] p-4 sm:p-5">
          <div className="label-cap text-[10px]">BBM / Konsumsi</div>
          <div data-testid="mileage-config" className="text-xs font-mono mt-2">
            {data ? <>Rp {(data.bbm_price || 0).toLocaleString("id-ID")}/L<br />{data.motor_consumption || 0} km/L</> : "—"}
          </div>
        </div>
      </div>

      <div className="bg-white border border-[#E4E4E7] overflow-x-auto">
        {loading && !data ? (
          <div className="p-10 text-center text-sm text-[#52525B]">Menghitung jarak… (pertama kali bisa agak lama)</div>
        ) : items.length === 0 ? (
          <div data-testid="mileage-empty" className="p-10 text-center text-sm text-[#52525B]">Tidak ada order pada bulan ini.</div>
        ) : (
          <table className="w-full text-sm min-w-[980px]">
            <thead>
              <tr className="border-b border-[#E4E4E7] text-left">
                {["Order", "Tanggal", "Pelanggan", "Teknisi", "Kantor Asal", "Kendaraan", "Jarak (km)", "PP (km)", "Liter", "Uang Bensin", ...(techId ? ["Reimbursement"] : []), ""].map((h) => (
                  <th key={h} className="px-3 py-2.5 label-cap text-[10px] whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody data-testid="mileage-table-body">
              {items.map((it) => {
                const editVal = edits[it.order_id];
                const editing = editVal !== undefined;
                return (
                  <tr key={rowKey(it)} className="border-b border-[#E4E4E7]/60 hover:bg-[#FAFAFA]">
                    <td className="px-3 py-2 font-mono text-xs font-bold whitespace-nowrap">
                      {it.order_no}
                      {it.session_no > 0 && <div className="text-[9px] text-[#7C3AED] font-sans font-bold">Sesi {it.session_no}/{it.session_total}</div>}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{it.schedule_date}</td>
                    <td className="px-3 py-2">
                      <button type="button" onClick={() => openCustomer(it.customer_id)} disabled={!it.customer_id} data-testid={`mileage-customer-${it.order_no}`} title="Klik untuk ubah data pelanggan (alamat / share loc)" className="font-bold text-xs text-left text-[#002FA7] hover:underline disabled:text-inherit disabled:no-underline">{it.customer_name}</button>
                      {it.city && <div className="text-[10px] text-[#A1A1AA]">{it.city}</div>}
                      {it.remark && (
                        <div data-testid={`mileage-remark-${it.order_no}`} title={it.remark} className={`inline-block mt-0.5 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 border ${(it.method === "no_coords" || it.method === "geocode_far") ? "border-[#DC2626]/40 text-[#DC2626] bg-[#DC2626]/5" : "border-[#D97706]/40 text-[#D97706] bg-[#D97706]/5"}`}>
                          {it.remark}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs whitespace-nowrap">{it.technician_name}</td>
                    <td className="px-3 py-2 text-xs whitespace-nowrap">{it.office_name}</td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 border ${it.vehicle === "mobil" ? "border-[#A1A1AA] text-[#52525B]" : "border-[#002FA7] text-[#002FA7]"}`}>{it.vehicle}</span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <input
                          type="number" step="0.1" min="0"
                          data-testid={`mileage-km-input-${it.order_no}`}
                          value={editing ? editVal : (it.km ?? "")}
                          placeholder="manual"
                          onChange={(e) => setEdits((p) => ({ ...p, [it.order_id]: e.target.value }))}
                          className={`w-20 px-2 py-1 border text-xs font-mono text-right outline-none focus:border-[#002FA7] ${it.km === null && !editing ? "border-[#DC2626] bg-[#DC2626]/5" : "border-[#E4E4E7]"}`}
                        />
                        {editing && editVal !== "" && Number(editVal) !== it.km && (
                          <button data-testid={`mileage-km-save-${it.order_no}`} disabled={savingId === it.order_id} onClick={() => saveManual(it.order_id, Number(editVal))} className="text-[#16A34A] hover:bg-[#16A34A]/10 p-1" title="Simpan jarak manual"><Check size={14} /></button>
                        )}
                        {it.manual && !editing && (
                          <button data-testid={`mileage-km-reset-${it.order_no}`} disabled={savingId === it.order_id} onClick={() => saveManual(it.order_id, null)} className="text-[#52525B] hover:bg-[#F4F4F5] p-1" title="Kembali ke otomatis"><Undo2 size={13} /></button>
                        )}
                      </div>
                      <div className={`text-[9px] mt-0.5 ${it.km === null ? "text-[#DC2626] font-bold" : "text-[#A1A1AA]"}`}>{METHOD_LABEL[it.method] || it.method}</div>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{it.km_pp !== null ? it.km_pp : "—"}</td>
                    <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{it.liters || "—"}</td>
                    <td className="px-3 py-2 font-mono text-xs font-bold whitespace-nowrap" data-testid={`mileage-cost-${it.order_no}`}>{it.cost ? formatIDR(it.cost) : "—"}</td>
                    {techId && (
                      <td className="px-3 py-2 text-xs" data-testid={`mileage-reim-${it.order_no}`}>
                        {firstRowOfDate.has(rowKey(it)) && (reimByDate[it.schedule_date] || []).length > 0 ? (
                          <div className="space-y-0.5">
                            {reimByDate[it.schedule_date].map((r, i) => (
                              <div key={i} className="whitespace-nowrap">
                                <span className="text-[9px] font-bold uppercase px-1 py-0.5 border border-[#7C3AED]/30 text-[#7C3AED] bg-[#7C3AED]/5 mr-1">{r.category || "reimburse"}</span>
                                <span className="font-mono font-bold">{formatIDR(r.amount)}</span>
                                {r.remark && <span className="text-[9px] text-[#A1A1AA] ml-1">{r.remark}</span>}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-[#A1A1AA]">—</span>
                        )}
                      </td>
                    )}
                    <td className="px-3 py-2 text-[10px] text-[#A1A1AA] whitespace-nowrap">{it.work_status}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {techId && orphanReims.length > 0 && (
        <div className="bg-white border border-[#E4E4E7] mt-4" data-testid="mileage-orphan-reims">
          <div className="px-4 py-2.5 border-b border-[#E4E4E7] label-cap text-[10px]">Reimbursement di tanggal tanpa order ({orphanReims.length})</div>
          <ul className="divide-y divide-[#E4E4E7]/60">
            {orphanReims.map((r, i) => (
              <li key={i} className="px-4 py-2 flex flex-wrap items-center gap-2 text-xs">
                <span className="font-mono">{r.date}</span>
                <span className="text-[9px] font-bold uppercase px-1 py-0.5 border border-[#7C3AED]/30 text-[#7C3AED] bg-[#7C3AED]/5">{r.category || "reimburse"}</span>
                <span className="font-mono font-bold">{formatIDR(r.amount)}</span>
                {r.remark && <span className="text-[#A1A1AA]">{r.remark}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
      {custEdit && (
        <CustomerFormModal
          key={custEdit.id}
          initial={custEdit.initial}
          editingId={custEdit.id}
          saving={custSaving}
          hint="Setelah disimpan, jarak tempuh order pelanggan ini dihitung ulang (jarak manual tetap). Ganti link Maps → koordinat lama otomatis dibuang."
          onClose={() => !custSaving && setCustEdit(null)}
          onSubmit={saveCustomer}
        />
      )}
    </Layout>
  );
}
